import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private isModuleLoadError(error: Error | null): boolean {
    if (!error) return false;
    const msg = error.message?.toLowerCase() || '';
    return (
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('loading chunk') ||
      msg.includes('loading css chunk') ||
      msg.includes('503')
    );
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleFullReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isModuleError = this.isModuleLoadError(this.state.error);

      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {isModuleError ? 'Failed to load page' : 'Something went wrong'}
            </AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">
                {isModuleError
                  ? 'A page module failed to load. This is usually temporary — please reload.'
                  : (this.state.error?.message || 'An unexpected error occurred')}
              </p>
              <div className="flex gap-2">
                <Button onClick={this.handleFullReload} variant="default">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload App
                </Button>
                <Button onClick={this.handleReset} variant="outline">
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
